import { useCallback, useEffect, useState } from 'react';

import LaunchIcon from '@mui/icons-material/Launch';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import MultipleDocumentUrlInputs from 'UI/components/MultipleDocumentUrlInputs/MultipleDocumentUrlInputs';
import Notes from 'UI/components/Notes/Notes';
import { navigateExternal, useT } from 'UI/lib';
import { getIsExternalUrl } from 'UI/lib/getUrlType';

import SelectedSkills from '../../../components/SelectedSkills';
import DateInput from '../../../DevelopmentPlan/AddMoreGoalsModals/components/DateInput';
import SubmitArea from '../../../DevelopmentPlan/AddMoreGoalsModals/components/SubmitArea';
import { useStatuses } from '../../useStatuses';
import { format } from 'date-fns';
import {
  AddCourseToPersonalDevelopmentDataInput,
  CourseLxp,
  MeasureStatusEnum,
  PdCourseUrlInput,
  Skill,
} from 'GQL/graphql';
import defaults from 'lodash/defaults';

interface AddCourseToPersonalPlanDialogProps {
  onFinish: () => void;
  onSubmit: (data: AddCourseToPersonalDevelopmentDataInput) => Promise<void>;
  course?: CourseLxp;
  selectedSkills?: Skill[];
  isEditMode?: boolean;
  isViewOnly?: boolean;
  initialData?: AddCourseToPersonalDevelopmentDataInput;
  measureId?: string;
}

const fallbackFormValues = (
  course: CourseLxp,
  courseUrl: PdCourseUrlInput
): AddCourseToPersonalDevelopmentDataInput => ({
  title: course ? course.title : '',
  description: course ? course.description.replace(/<[^>]+>/g, '') : '',
  startedAt: format(new Date(), 'yyyy-MM-dd'),
  documentUrl: '',
  notes: '',
  status: MeasureStatusEnum.Planned,
  courseUrl,
});

export const AddCourseToPersonalPlanDialog = ({
  onFinish,
  onSubmit,
  course,
  selectedSkills = [],
  isEditMode = false,
  isViewOnly = false,
  initialData,
  measureId,
}: AddCourseToPersonalPlanDialogProps) => {
  const t = useT();
  const statuses = useStatuses();
  const [mergedUrls, setMergedUrls] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  let courseUrl: PdCourseUrlInput = {
    url: '',
    isEditable: true,
  };

  if (course && !initialData) {
    courseUrl.url = course.url;
    courseUrl.isEditable = false;
  }

  const [form, setForm] = useState(() =>
    defaults(initialData, fallbackFormValues(course, courseUrl))
  );

  useEffect(() => {
    setForm({ ...form, documentUrl: mergedUrls });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mergedUrls]);

  const handleSubmit = useCallback(async () => {
    const newData = {
      ...form,
      courseId: measureId || course?.id,
      skillSynonymIds: selectedSkills?.map(({ synonym }) => synonym.synonymId ?? synonym._id) ?? [],
    };

    try {
      setIsSubmitting(true);
      await onSubmit(newData);
      onFinish();
    } finally {
      setIsSubmitting(false);
    }
  }, [form, measureId, course?.id, selectedSkills, onSubmit, onFinish]);

  const setFormOnChange = useCallback((field, value) => setForm({ ...form, [field]: value }), [
    form,
  ]);

  const handleChange = useCallback((field, value) => setFormOnChange(field, value), [
    setFormOnChange,
  ]);

  const openLink = useCallback(() => {
    let navigateUrl = form.courseUrl?.url;
    if (!getIsExternalUrl(navigateUrl)) {
      navigateUrl = 'http://' + navigateUrl;
    }

    navigateExternal(navigateUrl, true);
  }, [form.courseUrl]);

  const isSubmitButtonEnabled = form.title && form.startedAt && !isSubmitting;

  return (
    <Grid container>
      <Grid item xs={12}>
        <Grid container spacing={10}>
          <Grid item xs={12}>
            <TextField
              variant="outlined"
              id="title"
              label={t(
                'restrict:personal_development.recommendations_add_knowledge_exch_to_plan_title'
              )}
              InputLabelProps={{
                shrink: true,
              }}
              type="text"
              required
              value={form.title}
              error={!Boolean(form.title)}
              onChange={e => handleChange('title', e.target.value)}
              disabled
              data-test="courses__title"
              multiline
              maxRows="2"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <DateInput
              id="startedAt"
              label={t(
                'restrict:personal_development.recommendations_add_knowledge_exch_to_plan_started_at'
              )}
              value={form.startedAt}
              error={!Boolean(form.startedAt)}
              helperText={t('restrict:personal_development.started_at_date.help_text')}
              onDateChange={(_, value) => handleChange('startedAt', value)}
              disabled={isViewOnly}
              data-test="courses__started-date"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              variant="outlined"
              id="status"
              onChange={e => handleChange('status', e.target.value)}
              value={form.status}
              disabled={isViewOnly}
              data-test="courses-status"
              label={t('restrict:pd.status_field')}
              InputLabelProps={{
                shrink: true,
              }}
            >
              {statuses.map(status => (
                <MenuItem key={status.value} value={status.value}>
                  {t(status.translationKey)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} data-test="skills-part">
            {selectedSkills && selectedSkills.length > 0 && (
              <Typography variant="body1">
                {t(
                  'restrict:personal_development.recommendations_add_knowledge_exch_to_plan_skills'
                )}
              </Typography>
            )}
            <SelectedSkills
              skills={selectedSkills}
              isEditMode={false}
              withInteractiveSkills={false}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              variant="outlined"
              id="courseUrl"
              label={t(
                'restrict:personal_development.recommendations_add_course_to_plan_document_courseUrl'
              )}
              type="url"
              value={form.courseUrl?.url}
              onChange={e => handleChange('courseUrl.url', e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {isViewOnly && form.courseUrl?.url && (
                      <IconButton>
                        <LaunchIcon onClick={() => openLink()} />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{
                shrink: true,
              }}
              disabled={isViewOnly || !form.courseUrl?.isEditable}
              data-test="courses__course-url"
              multiline
              maxRows="2"
            />
          </Grid>

          <Grid item xs={12}>
            <MultipleDocumentUrlInputs
              setIsValidExternalUrl={undefined}
              mergeUrls={setMergedUrls}
              isDisabled={isViewOnly}
              urls={form.documentUrl}
              label={t(
                'restrict:personal_development.recommendations_add_knowledge_exch_to_plan_document_url'
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              variant="outlined"
              id="description"
              label={t(
                'restrict:personal_development.recommendations_add_course_to_plan_description'
              )}
              multiline
              maxRows={3}
              value={form.description}
              onChange={e => handleChange('description', e.target.value)}
              disabled
              data-test="courses__description-url"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Notes
              value={form.notes}
              onValueChange={value => handleChange('notes', value)}
              label={t(
                'restrict:personal_development.recommendations_add_knowledge_exch_to_plan_notes'
              )}
              disabled={isViewOnly}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} mt={20}>
        <SubmitArea
          isDisabled={isViewOnly}
          onFinish={onFinish}
          isSubmitButtonEnabled={isSubmitButtonEnabled}
          buttonLabel={isEditMode ? t('restrict:pd.update_goals.save_button') : t('common:add')}
          onSubmit={handleSubmit}
          tooltipLabel={t(
            'restrict:pd.add_further_goals.add_manually.submit_button_disabled.tooltip'
          )}
        />
      </Grid>
    </Grid>
  );
};
